import { useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEventEmitter, { emitEvent } from "../../../hooks/useEventEmitter";
import useEditVoiceNoteMutation from "../hooks/useEditVoiceNoteMutation";
import { EDIT_VOCE_NOTE_REQUEST } from "../types/RequestTypes";
import { VOICE_NOTE_TIMELINE } from "../types/CommonTypes";
import { fireSaveAndMoveNextTimelineEvent } from "./SaveAndMoveNextTimeline";
import SideDrawer, { SideDrawerBody } from "../../../components/SideDrawer";
import InputText from "../../../components/InputText";
import { detectChange } from "../../../helpers/detectChange";
import Divider from "../../../components/Divider";
import { updateCaseSummaryVoiceNoteInCache } from "../hooks/useGetCaseSummaryQuery";
import { updateCaseVoiceNoteDetailInCache } from "../hooks/useGetCaseDetailQuery";
import isNotNull from "../../../helpers/isNotNull";
import EditCaseSummaryDrawerFooter from "./EditCaseSummaryDrawerFooter";

const EDIT_VOICE_NOTE_DRAWER_EVENT = "EDIT_VOICE_NOTE_DRAWER_EVENT";

interface EditVoiceNoteDrawerProps {
  caseId?: number;
  voiceNote?: VOICE_NOTE_TIMELINE;
  timelineItemIndex?: number;
}

export const toggleEditVoiceNoteDrawer = (data?: EditVoiceNoteDrawerProps) => {
  emitEvent(EDIT_VOICE_NOTE_DRAWER_EVENT, data);
};

const EditVoiceNoteDrawer = () => {
  const { t } = useTranslation();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<EditVoiceNoteDrawerProps>();
  const { timelineItemIndex, voiceNote, caseId } = detail ?? {};
  const { control, watch, reset, handleSubmit } = useForm();

  const onClose = useCallback(() => {
    reset();
    setIsDrawerOpen(false);
  }, [reset]);

  const { mutate: editVoiceNoteMutate, isPending: isEditingVoiceNote } =
    useEditVoiceNoteMutation();

  const setFormData = useCallback(
    (data?: EditVoiceNoteDrawerProps) => {
      const formDefaultValues = {
        note: data?.voiceNote?.note ?? "",
      };
      reset(formDefaultValues);
    },
    [reset]
  );

  useEventEmitter(
    EDIT_VOICE_NOTE_DRAWER_EVENT,
    (data?: EditVoiceNoteDrawerProps) => {
      setIsDrawerOpen((prev) => !prev);
      setDetail(data);
      setFormData(data);
    }
  );

  const isSubmitDisabled = useMemo(() => {
    const isNoteChanged = detectChange(voiceNote?.note, watch("note"));
    return !watch("note") || !isNoteChanged || isEditingVoiceNote;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("note"), voiceNote?.note, isEditingVoiceNote]);

  const onEditVoiceNoteSuccess = (
    res: any,
    _req: EDIT_VOCE_NOTE_REQUEST,
    isSaveAndMoveNext?: boolean
  ) => {
    onClose();
    if (!res?.data?.caseId) {
      return;
    }

    updateCaseSummaryVoiceNoteInCache({
      caseId: res?.data?.caseId,
      data: {
        id: res?.data?.id,
        note: res?.data?.note,
        updatedAt: res?.data?.updatedAt ?? new Date().toISOString(),
        edited: true,
        loggedBy: res?.data?.loggedBy ?? voiceNote?.loggedBy,
        loggedById: res?.data?.loggedById,
        revisionLogs: res?.data?.revisionLogs,
      },
    });

    updateCaseVoiceNoteDetailInCache({
      caseId: res?.data?.caseId,
      tenantId: res?.data?.tenantId,
      hospitalId: res?.data?.hospitalId,
      data: {
        id: res?.data?.id,
        note: res?.data?.note,
        updatedAt: res?.data?.updatedAt ?? new Date().toISOString(),
        edited: true,
        loggedBy: res?.data?.loggedBy ?? voiceNote?.loggedBy,
        loggedById: res?.data?.loggedById,
        revisionLogs: res?.data?.revisionLogs,
      },
    });

    if (isSaveAndMoveNext && isNotNull(timelineItemIndex)) {
      fireSaveAndMoveNextTimelineEvent(timelineItemIndex);
    }
  };

  const onSubmit = (isSaveAndMoveNext?: boolean) =>
    handleSubmit((val) => {
      editVoiceNoteMutate(
        {
          noteId: voiceNote?.id,
          caseId: caseId,
          note: val.note,
        },
        {
          onSuccess: (res, req) =>
            onEditVoiceNoteSuccess(res, req, isSaveAndMoveNext),
        }
      );
    })();

  return (
    <SideDrawer
      visible={isDrawerOpen}
      onClose={onClose}
      title={t("Edit_Voice_Note")}
    >
      <SideDrawerBody>
        {/* Content */}
        <InputText
          control={control}
          name="note"
          label={t("Voice_Note_Text")}
          multiline
          rows={8}
          fullWidth
          autoFocus
          isLabelDefaultBehaviour
          sx={{ borderRadius: 1 }}
        />

        {/* Footer */}
        <Divider sx={{ mt: 8, mb: 2, mx: -3 }} />
        <EditCaseSummaryDrawerFooter
          onSubmit={onSubmit}
          isSubmitDisabled={isSubmitDisabled}
        />
      </SideDrawerBody>
    </SideDrawer>
  );
};

export default EditVoiceNoteDrawer;
